// WebAuthn (biometrie empreinte / Face ID) — utilitaires navigateur
import { API } from "./api";

export function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "Android";
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "iOS";
  return "Desktop";
}

export const isMobile = () => detectPlatform() !== "Desktop";

export async function isBiometricAvailable() {
  if (!window.PublicKeyCredential) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

function b64urlToBuf(b64url) {
  let b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out.buffer;
}

function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function post(path, body) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || "Erreur serveur");
  return data;
}

export const PASSKEY_FLAG = "fodindin_passkey_enrolled";

function friendlyWebAuthnError(e, mode) {
  if (e?.name === "NotAllowedError") {
    return new Error(
      mode === "login"
        ? "Aucune cle d'acces disponible sur cet appareil (ou operation annulee). Connectez-vous avec votre mot de passe, puis activez l'empreinte dans Parametres → Securite biometrique."
        : "Operation annulee ou empreinte non disponible. Verifiez qu'une empreinte digitale est bien enregistree dans les reglages de votre telephone (Parametres → Securite → Empreintes)."
    );
  }
  if (e?.name === "SecurityError") {
    return new Error("Domaine non autorise pour la biometrie. Reessayez depuis https://fodindin.com.");
  }
  if (e?.name === "InvalidStateError") {
    return new Error("Une empreinte est deja enregistree pour ce compte sur cet appareil. Desactivez-la puis reactivez-la si besoin.");
  }
  if (e?.name === "AbortError") {
    return new Error("Operation interrompue. Reessayez.");
  }
  return e;
}

export async function enrollBiometric() {
  const { publicKey } = await post("/me/biometric/register/options");
  const options = {
    ...publicKey,
    challenge: b64urlToBuf(publicKey.challenge),
    user: { ...publicKey.user, id: b64urlToBuf(publicKey.user.id) },
    excludeCredentials: (publicKey.excludeCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  let cred;
  try {
    cred = await navigator.credentials.create({ publicKey: options });
  } catch (e) {
    throw friendlyWebAuthnError(e, "enroll");
  }
  if (!cred) throw new Error("Enregistrement annule");
  const response = cred.response;
  const result = await post("/me/biometric/register/verify", {
    device_label: detectPlatform(),
    credential: {
      id: cred.id,
      type: cred.type,
      rawId: bufToB64url(cred.rawId),
      response: {
        attestationObject: bufToB64url(response.attestationObject),
        clientDataJSON: bufToB64url(response.clientDataJSON),
      },
      clientExtensionResults: cred.getClientExtensionResults(),
    },
  });
  localStorage.setItem(PASSKEY_FLAG, "1");
  return result;
}

export async function verifyBiometric() {
  const { publicKey } = await post("/me/biometric/auth/options");
  const options = {
    ...publicKey,
    challenge: b64urlToBuf(publicKey.challenge),
    allowCredentials: (publicKey.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  let cred;
  try {
    cred = await navigator.credentials.get({ publicKey: options });
  } catch (e) {
    throw friendlyWebAuthnError(e, "verify");
  }
  if (!cred) throw new Error("Verification annulee");
  return post("/me/biometric/auth/verify", { credential: serializeAssertion(cred) });
}

export async function loginWithPasskey() {
  const { publicKey } = await post("/auth/passkey/options");
  const options = {
    ...publicKey,
    challenge: b64urlToBuf(publicKey.challenge),
    allowCredentials: (publicKey.allowCredentials || []).map((c) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  let cred;
  try {
    cred = await navigator.credentials.get({ publicKey: options });
  } catch (e) {
    throw friendlyWebAuthnError(e, "login");
  }
  if (!cred) throw new Error("Connexion annulee");
  const result = await post("/auth/passkey/verify", { credential: serializeAssertion(cred) });
  localStorage.setItem(PASSKEY_FLAG, "1");
  return result;
}

function serializeAssertion(cred) {
  const response = cred.response;
  return {
    id: cred.id,
    type: cred.type,
    rawId: bufToB64url(cred.rawId),
    response: {
      authenticatorData: bufToB64url(response.authenticatorData),
      clientDataJSON: bufToB64url(response.clientDataJSON),
      signature: bufToB64url(response.signature),
      userHandle: response.userHandle ? bufToB64url(response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}
